import { DialogPanel } from '@headlessui/react';
import { GQLMerchTileFragment } from '@theprintbar/graphql';
import { Suspense, createContext, lazy, useContext, useMemo, useState } from 'react';
import { Modal } from '../Modal';

type PreviewPayload = {
	merch: GQLMerchTileFragment;
	colourCode?: string;
};

type PreviewContextValue = {
	preview: PreviewPayload | null;
	openPreview: (payload: PreviewPayload) => void;
	closePreview: () => void;
};

const PreviewContext = createContext<PreviewContextValue | null>(null);

export function useGlobalPreview() {
	const context = useContext(PreviewContext);
	if (!context) throw new Error('useGlobalPreview must be used within GlobalPreviewProvider');
	return context;
}

const MerchViewer = lazy(() => import('../MerchViewer').then(module => ({ default: module.MerchViewer })));

export function GlobalPreviewModal({ webDomain }: { webDomain?: string }) {
	const { preview, closePreview } = useGlobalPreview();

	return (
		<Modal open={!!preview} onClose={closePreview} customPanel>
			<DialogPanel className="w-full max-w-lg bg-white rounded-2xl">
				{!!preview && (
					<Suspense fallback={<div />}>
						<MerchViewer simple merch={preview.merch} colourCode={preview.colourCode} webDomain={webDomain} />
					</Suspense>
				)}
			</DialogPanel>
		</Modal>
	);
}

export function GlobalPreviewProvider({ children, webDomain }: { children: React.ReactNode; webDomain?: string }) {
	const [preview, setPreview] = useState<PreviewPayload | null>(null);

	const value = useMemo(
		() => ({
			preview,
			openPreview: (payload: PreviewPayload) => setPreview(payload),
			closePreview: () => setPreview(null),
		}),
		[preview],
	);

	return (
		<PreviewContext.Provider value={value}>
			{children}
			<GlobalPreviewModal webDomain={webDomain} />
		</PreviewContext.Provider>
	);
}
